import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Chip,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import { Card, CardBody, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import Pagination from '../Pagination/Pagination';
import QueueAssignDialog from '../DealsStatus/QueueAssignDialog';
import ConfirmDialog from '../../modals/ConfirmDialog';
import { Commercial } from '../../PanelComerciales.types';
import { panelTheme } from '../../PanelComerciales.theme';
import { QueueItem, QueuePage } from 'src/redux/panel_comerciales/panel_comerciales.types';

const NO_SELLER = 0;

/** Días transcurridos desde una fecha ISO (calendario, para la cola). */
export const daysSince = (iso: string | null): number => {
    if (!iso) return 0;
    const created = new Date(iso).getTime();
    if (Number.isNaN(created)) return 0;
    const msPerDay = 86_400_000;
    return Math.max(0, Math.floor((Date.now() - created) / msPerDay));
};

interface QueueSectionProps {
    page: QueuePage;
    loading: boolean;
    commercials: Commercial[];
    actionLoading: boolean;
    onLoadPage: (offset: number) => void;
    onAssign: (item: QueueItem, sellerId: number) => void | Promise<void>;
    onAssignBatch: (usersIds: number[], sellerId: number) => void | Promise<void>;
}

const EMPTY_CELL: React.CSSProperties = {
    textAlign: 'center',
    padding: 24,
    color: '#9CA3AF',
};

/**
 * Cola de asignación PENDING (sección propia, ago-2026): paginada, con
 * asignación individual (dialog) y asignación MÚLTIPLE (checkbox por fila +
 * "seleccionar todos" de la página + comercial en la barra superior).
 */
const QueueSection: React.FC<QueueSectionProps> = ({
    page,
    loading,
    commercials,
    actionLoading,
    onLoadPage,
    onAssign,
    onAssignBatch,
}) => {
    const [assigningItem, setAssigningItem] = useState<QueueItem | null>(null);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [batchSellerId, setBatchSellerId] = useState<number>(NO_SELLER);
    const [confirmingBatch, setConfirmingBatch] = useState(false);

    const activeCommercials = useMemo(
        () => commercials.filter((c) => c.active),
        [commercials],
    );
    const pageIds = useMemo(() => page.items.map((i) => i.usersId), [page.items]);

    // La selección es por página: al cambiar de página se limpia.
    useEffect(() => {
        setSelected(new Set());
    }, [page.offset]);

    const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const someSelected = pageIds.some((id) => selected.has(id));

    const toggleOne = (usersId: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(usersId)) next.delete(usersId);
            else next.add(usersId);
            return next;
        });
    };

    // Seleccionar/limpiar todos los de la PÁGINA visible (nota de negocio:
    // marcar = todos los de la página; desmarcar = limpia lo seleccionado).
    const toggleAllOnPage = () => {
        setSelected(allSelected ? new Set() : new Set(pageIds));
    };

    const handleIndividual = async (item: QueueItem, sellerId: number) => {
        await onAssign(item, sellerId);
        setAssigningItem(null);
    };

    const confirmBatch = async () => {
        setConfirmingBatch(false);
        await onAssignBatch(Array.from(selected), batchSellerId);
        setSelected(new Set());
    };

    const batchSellerName =
        activeCommercials.find((c) => c.id === batchSellerId)?.name ?? '';
    const canBatch =
        selected.size > 0 && batchSellerId !== NO_SELLER && !actionLoading;

    return (
        <Card>
            <SectionHeader
                title='Deals en cola'
                subtitle='Clientes esperando asignación; puedes asignar individual o en lote.'
                icon={<PlaylistAddCheckOutlinedIcon style={{ color: panelTheme.colors.indigo }} />}
                actions={
                    <Pagination
                        total={page.total}
                        limit={page.limit}
                        offset={page.offset}
                        onChange={onLoadPage}
                        disabled={loading}
                        label='de la cola'
                    />
                }
            />
            <CardBody>
                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginBottom: 12,
                    }}
                >
                    <TextField
                        select
                        size='small'
                        label='Asignar seleccionados a'
                        value={batchSellerId}
                        onChange={(e) => setBatchSellerId(Number(e.target.value))}
                        sx={{ minWidth: 260 }}
                    >
                        <MenuItem value={NO_SELLER} disabled>
                            Selecciona un comercial
                        </MenuItem>
                        {activeCommercials.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name} · {c.team}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant='contained'
                        size='small'
                        startIcon={<GroupAddOutlinedIcon />}
                        disabled={!canBatch}
                        onClick={() => setConfirmingBatch(true)}
                        sx={{
                            textTransform: 'none',
                            background: panelTheme.colors.indigo,
                            '&:hover': { background: panelTheme.colors.violet },
                        }}
                    >
                        Asignar seleccionados ({selected.size})
                    </Button>
                </div>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: 36 }}>
                                    <Checkbox
                                        size='small'
                                        checked={allSelected}
                                        indeterminate={!allSelected && someSelected}
                                        onChange={toggleAllOnPage}
                                        disabled={pageIds.length === 0}
                                        inputProps={{
                                            'aria-label': 'Seleccionar todos los de la página',
                                        }}
                                    />
                                </th>
                                <th>Cliente</th>
                                <th>Flujo / Tablero</th>
                                <th>Tier</th>
                                <th>Intentos</th>
                                <th>Días en cola</th>
                                <th>Comercial sugerido</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page.items.map((item) => (
                                <tr key={item.queueId}>
                                    <td>
                                        <Checkbox
                                            size='small'
                                            checked={selected.has(item.usersId)}
                                            onChange={() => toggleOne(item.usersId)}
                                            inputProps={{
                                                'aria-label': `Seleccionar cliente ${item.usersId}`,
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <strong>{item.clientName ?? `Cliente ${item.usersId}`}</strong>
                                        <br />
                                        <span style={{ fontSize: 12, color: '#6B7280' }}>
                                            {item.clientEmail ?? '—'}
                                        </span>
                                    </td>
                                    <td>{item.pipeline ?? item.flowType ?? '—'}</td>
                                    <td>{item.tier ?? '—'}</td>
                                    <td>{item.attempts ?? 0}</td>
                                    <td>{daysSince(item.createdAt)}</td>
                                    <td>
                                        {item.suggestedSeller ? (
                                            <Tooltip
                                                title={`Estimado por el motor (regla ${item.suggestedSeller.rule}); puede variar al asignar.`}
                                                arrow
                                            >
                                                <Chip
                                                    size='small'
                                                    label={item.suggestedSeller.name}
                                                    variant='outlined'
                                                />
                                            </Tooltip>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Asignar ahora'>
                                            <span>
                                                <IconButton
                                                    size='small'
                                                    aria-label='Asignar ahora'
                                                    disabled={actionLoading}
                                                    onClick={() => setAssigningItem(item)}
                                                >
                                                    <PersonAddAltOutlinedIcon fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {page.items.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={EMPTY_CELL}>
                                        No hay clientes esperando en la cola.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            <QueueAssignDialog
                item={assigningItem}
                commercials={commercials}
                busy={actionLoading}
                onClose={() => setAssigningItem(null)}
                onConfirm={handleIndividual}
            />
            <ConfirmDialog
                open={confirmingBatch}
                title='¿Asignar los seleccionados?'
                description={
                    `Se asignarán ${selected.size} deal(s) a ${batchSellerName}. ` +
                    'Esto sobrescribe el comercial actual de cada uno.'
                }
                confirmLabel='Sí, asignar'
                cancelLabel='Cancelar'
                onConfirm={confirmBatch}
                onCancel={() => setConfirmingBatch(false)}
            />
        </Card>
    );
};

export default QueueSection;
